import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from '../context/ConsentContext';
import useAuth from './useAuth';

// Fires a `page_view` GA4 event whenever the SPA route changes. Mounted
// once at App.jsx top level so navigation across any route triggers it.
//
// gtag is configured with `send_page_view: false` (see utils/analytics/gtag.js)
// so the script's own auto-pageview doesn't double up with this.
//
// Why role-based skip (and not path-based as before):
// The previous version blacklisted any path starting with `/salon`, but
// that net is too wide — the *public* salon detail page lives at
// `/salon/:slug` (e.g. `/salon/bubble-pet`), which is exactly where
// clients land from search or external links. Excluding the whole
// `/salon` prefix dropped ~30–50% of real marketplace funnel traffic
// from the report. Switching to a role check keeps the original intent
// (don't count operator activity in the public funnel) while letting
// anonymous and CLIENT visitors flow through correctly across every
// public route, including `/salon/:slug` and `/admin/login`.
//
// Wait for auth to finish loading before firing. AuthContext starts
// with loading=true, user=null while it rehydrates the session from
// localStorage + checkAuth — if we fire during that window, an admin
// or salon-owner returning with a valid token gets counted as an
// anonymous pageview (user is null at that instant, so the role
// guard short-circuits). The `loading` flag flips false once the
// session is confirmed (or definitively rejected), and the effect
// re-runs because it's in the deps array.
const SKIPPED_ROLES = new Set(['SALON_OWNER', 'ADMIN']);

// Accepts an optional location override so the page_view fires for the
// page actually shown (the trailing view-transition location) rather than
// one commit early; falls back to useLocation() when omitted.
export default function useGA4PageView(locationOverride) {
  const current = useLocation();
  const { pathname, search } = locationOverride ?? current;
  const { analytics } = useConsent();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!analytics) return;
    if (typeof window.gtag !== 'function') return;
    if (user && SKIPPED_ROLES.has(user.role)) return;

    window.gtag('event', 'page_view', {
      page_path: pathname + (search || ''),
      // Build page_location from the resolved pathname/search too, not
      // window.location.href: when a trailing view-transition location is
      // passed, the live href has already advanced to the next route, which
      // would disagree with page_path.
      page_location: new URL(pathname + (search || ''), window.location.origin).href,
      page_title: document.title,
    });
  }, [pathname, search, analytics, user, loading]);
}
